/**
 * CHEM1131 Experiment Configurations
 * General Chemistry Laboratory
 */

import type { ExperimentConfig } from '@/types';

// Import experiment configurations (experiments 1-6)
import experiment1Config, {
  householdCompoundColors,
  householdCompounds
} from './experiment1.config';
import experiment2Config, {
  cationColors,
  cationData
} from './experiment2.config';
import experiment3Config, {
  anionColors,
  anionData
} from './experiment3.config';
import experiment4Config, {
  molarMassColors,
  compoundLibrary
} from './experiment4.config';
import experiment5Config, {
  copperCycleColors,
  reactions as copperCycleReactions
} from './experiment5.config';
import experiment6Config, {
  saltFormationColors,
  metalReactivity,
  doubleDisplacementReactions
} from './experiment6.config';

// Import new experiment configurations (experiments 7-11)
import experiment7Config, {
  PRECIPITATE_COLORS,
  CATION_DATA
} from './experiment7.config';
import experiment8Config, {
  REACTION_COLORS as EXP8_REACTION_COLORS,
  GAS_EVOLUTION_RATES,
  CRYSTAL_DATA
} from './experiment8.config';
import experiment9Config, {
  PHENOLPHTHALEIN_COLORS,
  SOLUTION_COLORS as EXP9_SOLUTION_COLORS,
  EXPECTED_VINEGAR_PERCENT
} from './experiment9.config';
import experiment10Config, {
  METHYL_ORANGE_COLORS,
  ANTACID_TYPES,
  MEQ_STANDARD
} from './experiment10.config';
import experiment11Config, {
  TEMPERATURE_COLORS,
  MATERIAL_COLORS,
  FUMES_DATA,
  DESICCATOR_COLORS,
  EXPECTED_RESULTS,
  ATOMIC_MASSES
} from './experiment11.config';

// Re-export experiment-specific data
export {
  // Experiment 1 - Household Compounds
  householdCompoundColors,
  householdCompounds,
  // Experiment 2 - Cation Analysis
  cationColors,
  cationData,
  // Experiment 3 - Anion Analysis
  anionColors,
  anionData,
  // Experiment 4 - Molar Mass
  molarMassColors,
  compoundLibrary,
  // Experiment 5 - Copper Cycle
  copperCycleColors,
  copperCycleReactions,
  // Experiment 6 - Salts Formation
  saltFormationColors,
  metalReactivity,
  doubleDisplacementReactions,
  // Experiment 7 - Salts Solubility
  PRECIPITATE_COLORS,
  CATION_DATA,
  // Experiment 8 - Aluminum Compound
  EXP8_REACTION_COLORS,
  GAS_EVOLUTION_RATES,
  CRYSTAL_DATA,
  // Experiment 9 - Acetic Acid Analysis
  PHENOLPHTHALEIN_COLORS,
  EXP9_SOLUTION_COLORS,
  EXPECTED_VINEGAR_PERCENT,
  // Experiment 10 - Antacid Analysis
  METHYL_ORANGE_COLORS,
  ANTACID_TYPES,
  MEQ_STANDARD,
  // Experiment 11 - Empirical Formula
  TEMPERATURE_COLORS,
  MATERIAL_COLORS,
  FUMES_DATA,
  DESICCATOR_COLORS,
  EXPECTED_RESULTS,
  ATOMIC_MASSES
};

// Export individual experiments
export {
  experiment1Config,
  experiment2Config,
  experiment3Config,
  experiment4Config,
  experiment5Config,
  experiment6Config,
  experiment7Config,
  experiment8Config,
  experiment9Config,
  experiment10Config,
  experiment11Config
};

// All CHEM1131 experiments indexed by ID
export const CHEM1131_EXPERIMENT_CONFIGS: Record<string, ExperimentConfig> = {
  exp1: experiment1Config,
  exp2: experiment2Config,
  exp3: experiment3Config,
  exp4: experiment4Config,
  exp5: experiment5Config,
  exp6: experiment6Config,
  exp7: experiment7Config,
  exp8: experiment8Config,
  exp9: experiment9Config,
  exp10: experiment10Config,
  exp11: experiment11Config,
};

// Get experiment config by ID
export function getExperimentConfig(experimentId: string): ExperimentConfig | undefined {
  return CHEM1131_EXPERIMENT_CONFIGS[experimentId];
}

// Get all available experiment configs (non-placeholder - have parts defined)
export function getAvailableExperiments(): ExperimentConfig[] {
  return Object.values(CHEM1131_EXPERIMENT_CONFIGS).filter(
    (config) => config.parts.length > 0
  );
}

// Check if an experiment is fully implemented
export function isExperimentImplemented(experimentId: string): boolean {
  const config = CHEM1131_EXPERIMENT_CONFIGS[experimentId];
  return config ? config.parts.length > 0 : false;
}

// Get list of implemented experiment numbers
export function getImplementedExperimentNumbers(): number[] {
  return Object.values(CHEM1131_EXPERIMENT_CONFIGS)
    .filter(config => config.parts.length > 0)
    .map(config => config.number)
    .sort((a, b) => a - b);
}

export default CHEM1131_EXPERIMENT_CONFIGS;
